import React from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../utils/cn';

/* ══════════════════════════════════════════════════════════════
   How a drive catchment is routed: the traffic, which way the roads are read
   (who can reach the place / what the place can reach) and whether the search
   may cross the country's border. The server reads them as the request's
   travel type (2, 4, 6, 7) and `routing: { direction, crossBorder }`.
   ══════════════════════════════════════════════════════════════ */

export type TrafficControl = 'nmltrf' | 'hvytrf' | 'vhvytrf' | 'notrf';
export type RouteDirection = 'to-here' | 'from-here';

export interface RoutingChoice {
  direction: RouteDirection;
  /** null: as the place's country is set up (Admin → Routing); true: across borders; false: stay in the country. */
  crossBorder: boolean | null;
}

export const DEFAULT_ROUTING: RoutingChoice = { direction: 'to-here', crossBorder: null };

/** The travel type a drive is asked with. */
export function driveTravelType(traffic: TrafficControl): number {
  switch (traffic) {
    case 'hvytrf': return 4;
    case 'vhvytrf': return 7;
    case 'notrf': return 6;
    default: return 2;
  }
}

/** What the request carries for the routing; nothing when it is all the defaults. */
export function routingPayload(routing?: RoutingChoice): { direction: RouteDirection; crossBorder?: boolean } | undefined {
  if (!routing) return undefined;
  if (routing.direction === DEFAULT_ROUTING.direction && routing.crossBorder === null) return undefined;
  return routing.crossBorder === null
    ? { direction: routing.direction }
    : { direction: routing.direction, crossBorder: routing.crossBorder };
}

const Pill: React.FC<{ active: boolean; onClick: () => void; title?: string; children: React.ReactNode }> = ({
  active, onClick, title, children,
}) => (
  <button
    type="button"
    title={title}
    onClick={onClick}
    className={cn(
      'px-2 py-0.5 rounded text-[10px] font-medium transition-colors border',
      active
        ? 'bg-gn-blue-light border-gn-blue text-gn-blue'
        : 'bg-gn-secondary border-gn-light text-gn-secondary hover:text-gn-primary hover:border-gn',
    )}
  >
    {children}
  </button>
);

interface Props {
  traffic: TrafficControl;
  onTraffic: (t: TrafficControl) => void;
  routing: RoutingChoice;
  onRouting: (r: RoutingChoice) => void;
  /** The traffic pills; off where a panel shows only walking or rings. */
  showTraffic?: boolean;
}

export const RoutingControls: React.FC<Props> = ({ traffic, onTraffic, routing, onRouting, showTraffic = true }) => {
  const { t } = useTranslation();
  return (
    <>
      {showTraffic && (
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[9px] text-gn-secondary mr-0.5">{t('analysis.traffic')}:</span>
          <Pill active={traffic === 'notrf'} onClick={() => onTraffic('notrf')}>{t('analysis.traffic_none')}</Pill>
          <Pill active={traffic === 'nmltrf'} onClick={() => onTraffic('nmltrf')}>{t('analysis.traffic_normal')}</Pill>
          <Pill active={traffic === 'hvytrf'} onClick={() => onTraffic('hvytrf')}>{t('analysis.traffic_heavy')}</Pill>
          <Pill active={traffic === 'vhvytrf'} onClick={() => onTraffic('vhvytrf')}>{t('analysis.traffic_very_heavy')}</Pill>
        </div>
      )}
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-[9px] text-gn-secondary mr-0.5">{t('analysis.direction')}:</span>
        <Pill active={routing.direction === 'to-here'} title={t('analysis.direction_to_here_hint')}
          onClick={() => onRouting({ ...routing, direction: 'to-here' })}>{t('analysis.direction_to_here')}</Pill>
        <Pill active={routing.direction === 'from-here'} title={t('analysis.direction_from_here_hint')}
          onClick={() => onRouting({ ...routing, direction: 'from-here' })}>{t('analysis.direction_from_here')}</Pill>
      </div>
      <div className="flex items-center gap-1 flex-wrap">
        <span className="text-[9px] text-gn-secondary mr-0.5">{t('analysis.border')}:</span>
        <Pill active={routing.crossBorder === null} title={t('analysis.border_country_hint')}
          onClick={() => onRouting({ ...routing, crossBorder: null })}>{t('analysis.border_country')}</Pill>
        <Pill active={routing.crossBorder === true}
          onClick={() => onRouting({ ...routing, crossBorder: true })}>{t('analysis.border_cross')}</Pill>
        <Pill active={routing.crossBorder === false}
          onClick={() => onRouting({ ...routing, crossBorder: false })}>{t('analysis.border_stay')}</Pill>
      </div>
    </>
  );
};
